import { useMutation } from '@tanstack/react-query';

import { UUID } from '~/types/common';

import { VestigasHTTPError } from '@vestigas/data';

import { queryClient } from '~/services/queryClient';

import { currentLocale } from '~/utils/i18n';

import { queryKeysSupplier } from '../queryKeys';
import {
  SuppliersPriorityResponse,
  PriorityItem,
} from '../useQuerySuppliersPriority';

import { putSuppliersPriority } from './putSuppliersPriority';
import {
  PutSuppliersPriorityPayload,
  PutSuppliersPriorityMutationOptions,
  PutSuppliersPriorityMutationContext,
} from './types';
import { zodPutSuppliersPriorityPayloadSchema } from './validations';

/**
 * Speed up interactions by setting the query cache immediately with the new configuration, without waiting for the server response.
 */
export function updateCacheOptimistically(
  previousCache: SuppliersPriorityResponse | undefined,
  payload: PutSuppliersPriorityPayload,
) {
  if (!previousCache) {
    return;
  }

  const validatedPayload =
    zodPutSuppliersPriorityPayloadSchema.safeParse(payload);
  if (!validatedPayload.success) {
    return;
  }

  const { highPriority, regularPriority } = validatedPayload.data;

  const suppliersMap = new Map<UUID, PriorityItem>();

  for (const supplier of previousCache.highPriority) {
    suppliersMap.set(supplier.dataExchangeId, supplier);
  }
  for (const supplier of previousCache.regularPriority) {
    suppliersMap.set(supplier.dataExchangeId, supplier);
  }

  const mentionedIds = new Set([...highPriority, ...regularPriority]);

  const newHighPriority = [
    ...highPriority.flatMap((id) => {
      const item = suppliersMap.get(id);
      return item ? [item] : [];
    }),
    ...previousCache.highPriority.filter(
      (item) => !mentionedIds.has(item.dataExchangeId),
    ),
  ];

  const collator = new Intl.Collator(currentLocale(), { sensitivity: 'base' });

  const newRegularPriority = [
    ...regularPriority.flatMap((id) => {
      const item = suppliersMap.get(id);
      return item ? [item] : [];
    }),
    ...previousCache.regularPriority.filter(
      (item) => !mentionedIds.has(item.dataExchangeId),
    ),
  ].sort((a, b) => collator.compare(a.supplier.name, b.supplier.name));

  const updatedCache = {
    ...previousCache,
    highPriority: newHighPriority,
    highPrioritySlotsRemaining: Math.max(
      0,
      previousCache.highPrioritySlotsTotal - newHighPriority.length,
    ),
    highPrioritySlotsUsed: newHighPriority.length,
    regularPriority: newRegularPriority,
  } satisfies SuppliersPriorityResponse;

  return updatedCache;
}

const MUTATION_KEY = ['updateSuppliersPriority'];

/**
 * React Query based custom hook for updating the supplier priority order.
 *
 * Applies an optimistic cache update on mutate and reconciles with the server
 * response on success. Rolls back to the pre-mutation cache on error.
 *
 * @param options - Optional mutation options.
 * @returns The mutation result.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Supplier-overview/operation/put_suppliers_priority_suppliers_views_priority_put
 */
export const useMutationPutSuppliersPriority = (
  options?: PutSuppliersPriorityMutationOptions,
) => {
  const { onError, ...mutationOptions } = options ?? {};

  // oxlint-disable sort-keys - TanStack Query needs the onError callback to come after onMutate!

  return useMutation<
    SuppliersPriorityResponse,
    VestigasHTTPError,
    PutSuppliersPriorityPayload,
    PutSuppliersPriorityMutationContext
  >({
    mutationKey: MUTATION_KEY,
    mutationFn: (payload) => putSuppliersPriority(payload),
    onMutate(payload: PutSuppliersPriorityPayload) {
      const queryKey = queryKeysSupplier.getPriority(
        payload.recipientCompanyIds[0],
      );

      const previousCache =
        queryClient.getQueryData<SuppliersPriorityResponse>(queryKey);

      const updatedCache = updateCacheOptimistically(previousCache, payload);

      if (updatedCache) {
        queryClient.setQueryData(queryKey, updatedCache);
      }

      // Always return the snapshot so onError can roll back even if the
      // optimistic update could not be applied.
      return { previousCache };
    },
    onError(error, variables, onMutateResult, context) {
      if (onMutateResult?.previousCache !== undefined) {
        queryClient.setQueryData(
          queryKeysSupplier.getPriority(variables.recipientCompanyIds[0]),
          onMutateResult.previousCache,
        );
      }

      onError?.(error, variables, onMutateResult, context);
    },
    onSuccess(data, variables) {
      const recipientCompanyId = variables.recipientCompanyIds[0];

      // Only count concurrent mutations targeting the same recipient company so
      // unrelated companies' in-flight mutations don't block this cache write.
      // === 1 means only this mutation is still running (about to finish)
      const areAllConcurrentMutationsFinished =
        queryClient.isMutating({
          mutationKey: MUTATION_KEY,
          predicate: (mutation) =>
            (
              mutation.state.variables as
                | PutSuppliersPriorityPayload
                | undefined
            )?.recipientCompanyIds[0] === recipientCompanyId,
        }) <= 1;

      if (areAllConcurrentMutationsFinished) {
        // Update the cache with the mutation response once all concurrent mutations are finished - no refetch necessary
        void queryClient.setQueryData(
          queryKeysSupplier.getPriority(variables.recipientCompanyIds[0]),
          data,
        );
      }
    },
    throwOnError: false,
    ...mutationOptions,
  });
};
